import {Component, OnInit, ViewChild, ViewEncapsulation} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { AppService } from "../../../app.service";
import { NotificationService } from "../../../core/services/notification.service";
import { ComponentPageBase } from "../../../shared/components/component-page-base/component-page-base";
import { Agreement } from "../../../shared/models/agreement/agreement";
import { AgreementsService } from "../../../shared/services/agreements.service";
import { StepperItem } from "../../../shared/components/stepper/app-stepper.component";
import { WizardBaseComponent } from "../../../shared/components/wizard-base/wizard-base.component";
import { NewRequestCustomerObjectInfoComponent } from "../../../requests/new-request/sub-components/customer-object-info/new-request-customer-object-info.component";
import { ContragentWithContactDto } from "../../../shared/models/contragent/contragent-with-contact-dto";
import { CustomerObjectsService } from "../../customer-objects/customer-objects.service";
import { KeyValueObject } from "../../../shared/models/core/KeyValueObject";
import { CustomerObjectWithContactsDto } from "../../../shared/models/customerObject/customer-object-with-contacts.dto";
import { ContragentsService } from "../../contragents/contragents.service";
import { AgreementPriceService } from "../services/agreement-price-service";
import { AgreementState } from "../../../shared/enums/agreement-state.enum";
import { AgreementChangeStatusDto } from "../../../shared/models/agreement/agrement-change-status.dto";
import { CustomerObjectServiceCallsService } from "../../customer-object-service-calls/customer-object-service-calls.service";
import { AgreementChangeStatusConfirmationComponent } from "../components/agreement-change-status-confirmation/agreement-change-status-confirmation.component";
import { DialogService } from "@progress/kendo-angular-dialog";
import { SecurityService } from "../../../core/services/security.service";


@Component({
	selector: 'agreement-page',
	templateUrl: './agreement.page.html',
	styleUrls: [
		'./agreement.page.scss',
		'../../../../vendor/libs/ngx-perfect-scrollbar/ngx-perfect-scrollbar.scss',
		'../../../../vendor/libs/angular2-ladda/angular2-ladda.scss'
	]
})
export class AgreementPage extends WizardBaseComponent {

	@ViewChild('customerObjectInfoBlock') customerObjectInfoBlock: NewRequestCustomerObjectInfoComponent;

	customerObjects: KeyValueObject[] = [];

	isNew: boolean = false;
	dataSaving: boolean = false;
	isReadonly: boolean = false;
	changeStatusLoading: boolean = false;
	createServiceRequestLoading: boolean = false;

	savedAgreement: string;
	agreement: Agreement = null;
	selectedCustomerObjectId: number;
	selectedContragent: ContragentWithContactDto = null;
	deadlineWasChanged: boolean = false;

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private appService: AppService,
		private agreementsService: AgreementsService,
		private customerObjectsService: CustomerObjectsService,
		private contragentsService: ContragentsService,
		private agreementPriceService: AgreementPriceService,
		private notificationService: NotificationService,
		private dialogService: DialogService,
		private customerObjectServiceCallsService: CustomerObjectServiceCallsService,
		private securityService: SecurityService
	) {
		super();
		this.appService.pageTitle = 'Договор';
	}

	initData() {
		let agreementId = this.route.snapshot.paramMap.get('agreementId');

		if (agreementId) {
			this.loadAgreement(+agreementId);
		}
		else {
			this.agreement = new Agreement();
			this.agreement.stateName = 'Черновик';
			this.isNew = true;
			this.buildSteps();
			this.applyInitialStepFromQuery();
		}
	}

	loadAgreement(agreementId: number, redirectToStep: boolean = true) {
		this.agreementsService.getById(agreementId).subscribe(resp => {
			this.contragentsService.getContragentWithContact(resp.entity.contragentId).subscribe(contragentResp => {
				this.selectedContragent = contragentResp;
				this.selectedCustomerObjectId = resp.entity.customerObjectId;
				this.agreement = resp.entity;
				this.isReadonly = this.agreement.stateId != AgreementState.draft;
				this.savedAgreement = JSON.stringify(this.agreement);
				this.buildSteps();
				this.refreshStepStatuses();

				if (redirectToStep && !this.applyInitialStepFromQuery()) {
					const lastStep = this.steps[this.steps.length - 1];
					if (lastStep) {
						this.currentStepNumber = lastStep.number;
					}
				}
			});
		});
	}

	private applyInitialStepFromQuery(): boolean {
		const stepId = this.route.snapshot.queryParamMap.get('step');
		if (!stepId || !this.steps.length) {
			return false;
		}

		const step = this.steps.find(s => s.id === stepId);
		if (step) {
			this.currentStepNumber = step.number;
			return true;
		}
	}

	buildSteps() {
		this.steps = [{
			id: 'step1',
			number: 1,
			name: 'Клиент и объект',
			isActive: true,
			isSuccess: false,
			isError: false,
			imageClass: 'request-client-info-icon',
			checkSuccess: () => this.agreement.contragentId != null && 
				this.agreement.customerObjectId != null && 
				this.agreement.serviceCenterId != null
		}, {
			id: 'step2',
			number: 2,
			name: 'Анализ воды',
			isActive: false,
			isSuccess: false,
			isError: false,
			imageClass: 'laboratory-icon',
			checkSuccess: () => true,
		}, {
			id: 'step3',
			number: 3,
			name: 'Оборудование',
			isActive: false,
			isSuccess: false,
			isError: false,
			imageClass: 'request-services-and-devices-icon',
			checkSuccess: () => !this.agreement.hasConflictDevices
		}, {
			id: 'step4',
			number: 4,
			name: 'Сроки договора',
			isActive: false,
			isSuccess: false,
			isError: false,
			imageClass: 'request-history-icon',
			checkSuccess: () => this.agreement.agreementDate && this.agreement.serviceInterval != null && this.agreement.duration != null
		}, {
			id: 'step5',
			number: 5,
			name: 'Услуги',
			isActive: false,
			isSuccess: false,
			isError: false,
			imageClass: 'request-services-list',
			checkSuccess: () => this.agreement.plannedServicesCount > 0
		}, {
			id: 'step6',
			number: 6,
			name: 'Расходные материалы',
			isActive: false,
			isSuccess: false,
			isError: false,
			imageClass: 'request-devices-list',
			checkSuccess: () => this.agreement.plannedDevicesCount > 0 || this.agreement.notRequireDevices == true
		}];

		let summaryStep = {
			id: 'step7',
			number: 7,
			name: 'Резюме',
			isActive: false,
			isSuccess: false,
			isError: false,
			imageClass: 'request-services-list',
			checkSuccess: () => this.steps.filter(x => x.number <= 6).every(x => x.checkSuccess())
		}
		this.steps.push(summaryStep);
	}

	moveToStep(step: StepperItem): void {
		if (this.savedAgreement) {
			this.agreement = JSON.parse(this.savedAgreement);
		}
		super.moveToStep(step);
	}

	goToPreviousStep(): void {
		if (this.savedAgreement) {
			this.agreement = JSON.parse(this.savedAgreement);
		}
		super.goToPreviousStep();
	}

	goToNextStep(): void {
		if (!this.isReadonly && this.securityService.isSpecialRightClaimValid('FullReadonly')) {
			return;
		}

		if (this.isReadonly) {
			this.refreshStepStatuses();
			if (!this.isLastStep) {
				this.currentStepNumber += 1;
			}
			return;
		}

		if (this.currentStepId == 'step4' && this.deadlineWasChanged) {
			this.notificationService.confirmation({
            title: 'Изменение сроков договора',
            message: `Изменение сроков договора приведет к перезаписи всех обслуживаний. Продолжить?`,
            confirmButtonText: 'Продолжить',
            cancelButtonText: 'Отмена'
			}, () => { 
				this.savedAgreement = JSON.stringify(this.agreement); 
				super.goToNextStep();
			}, () => { });
		}
		else {
			this.savedAgreement = JSON.stringify(this.agreement); 
			super.goToNextStep();
		}		
	}

	saveStepChanges(afterAction: () => void) {
        this.saveChangesImpl(afterAction);
    }

    saveChanges() {
        this.saveChangesImpl(() => {});
    }

	saveChangesImpl(afterAction: () => void) {
		if (this.securityService.isSpecialRightClaimValid('FullReadonly')) {
			return;
		}

		this.dataSaving = true;

		if (this.agreement.agreementId) {
			this.agreement.isFinalCommit = this.isLastStep;
			this.agreementsService.update(this.agreement, '', false, true).subscribe((resp) => {
				this.dataSaving = false;
				if (resp.isSuccessful) {
					afterAction();
					this.agreementPriceService.notifyPriceChanged();

					if (this.agreement.isFinalCommit) {
						this.loadAgreement(this.agreement.agreementId, false);
					}
				}
			}, () => this.dataSaving = false)
		}
		else {
			this.agreementsService.create(this.agreement, null, false, true).subscribe(resp => {
				this.agreement.agreementId = resp.data;
				this.dataSaving = false;
				if (resp.isSuccessful) {
					const nextStepNumber = this.steps.length
						? Math.min(this.currentStepNumber + 1, this.steps.length)
						: this.currentStepNumber + 1;
					const nextStepId = this.steps.find(x => x.number == nextStepNumber).id;
					this.router.navigate(['/categories/agreement', resp.data], {
						queryParams: { step: nextStepId }
					});
					//afterAction();
					//this.agreementPriceService.notifyPriceChanged();
				}
			});
		}
	}

	saveContragent(contragent: ContragentWithContactDto) {
		this.selectedContragent = contragent;
		if (contragent == null) {
			this.customerObjectInfoBlock.customerObjectChange(null);
			this.agreement.contragentId = null;
			this.agreement.customerObjectId = null;
		}
		else {
			if (this.agreement.contragentId != contragent.id) {
				this.customerObjectInfoBlock.customerObjectChange(null);
				this.agreement.customerObjectId = null;
			}
			this.agreement.contragentId = contragent.id;
			this.loadCustomerObjects();
		}
	}

	loadCustomerObjects() {
		this.customerObjectsService.getCustomerObjectsByContragent(this.agreement.contragentId)
			.subscribe((resp) => {
				this.customerObjects = resp;
				if (resp.length == 1) {
					this.customerObjectInfoBlock.customerObjectChange(resp[0].id);
				}
			});
	}

	saveCustomerObject(customerObject: CustomerObjectWithContactsDto) {
		if (customerObject) {
			this.selectedCustomerObjectId = customerObject.customerObjectId;
			this.customerObjectsService.getCustomerObjectsByContragent(this.agreement.contragentId)
				.subscribe((resp) => {
					this.customerObjects = resp;
					this.agreement.customerObjectId = customerObject.customerObjectId;
				});
		}
		else {
			this.agreement.customerObjectId = null;
		}
	}

	saveServiceCenter(serviceCenterId: number) {
		this.agreement.serviceCenterId = serviceCenterId;
	}

	setDeadlineChanges(value: boolean) {
		this.deadlineWasChanged = value;
	}

	cancelAgreement() {
		const dialogRef = this.dialogService.open({
			content: AgreementChangeStatusConfirmationComponent,
			title: 'Отмена договора',
			width: '480px',
			height: '320px',
		});

		const confirmationComponent = <AgreementChangeStatusConfirmationComponent>dialogRef.content.instance;
		confirmationComponent.title = 'Прекратить действие договора?';
		confirmationComponent.cancelButtonText = 'Назад';
		confirmationComponent.confirmButtonText = 'Прекратить';
		confirmationComponent.onCancelEvent.subscribe(() => {
			dialogRef.close();
		});
		confirmationComponent.onConfirmEvent.subscribe((comment: string) => {
			dialogRef.close();
			this.cancelAgreementImpl(comment);
		});
	}

	cancelAgreementImpl(userCommentText: string) {
		this.dataSaving = true;
		this.changeStatusLoading = true;

		let changeStatusModel = new AgreementChangeStatusDto;
		changeStatusModel.agreementId = this.agreement.agreementId;
		changeStatusModel.newStateId = <number>AgreementState.cancelled;

		this.agreementsService.changeStatus(changeStatusModel).subscribe(() => {
			let commentText = userCommentText != null
				? `Действие договора было прекращено. ${userCommentText}`
				: 'Действие договора было прекращено.'
			this.customerObjectServiceCallsService.createComment(this.agreement.customerObjectId, commentText).subscribe(() => { });
			this.agreementsService.getById(this.agreement.agreementId).subscribe(resp => {
				this.agreement = resp.entity;
				this.savedAgreement = JSON.stringify(this.agreement);

				this.dataSaving = false;
				this.changeStatusLoading = false;
			});
		}, () => {
			this.dataSaving = false;
			this.changeStatusLoading = false;
		});
	}

	createFirstServiceRequest() {
        this.createServiceRequestLoading = true;

        this.agreementsService.createRequestByStartAgreementMaintenance(this.agreement.agreementId).subscribe(requestData => {
            if (!requestData.isSuccessful) {
                this.createServiceRequestLoading = false;
                return;
            }

            this.notificationService.success({
                title: 'Создание заявки',
                message: 'Создание заявки на первое ТО успешно'
            });

            window.open(`/requests/${requestData.data}`, '_blank');
            this.createServiceRequestLoading = false;

        }, () => this.createServiceRequestLoading = false);
    }

	get defaultPageHeight(): number {
		return window.innerHeight - 200;
	}

	get agreementIsActive(): boolean {
		return this.agreement.stateId == <number>AgreementState.active;
	}

	get canCreateFirstServiceRequest(): boolean {
		return this.agreement.stateId == <number>AgreementState.active || this.agreement.stateId == <number>AgreementState.waitingPayment;
	}
}
